/**
 * Table Sizing Composable
 *
 * Manages table cell sizing with responsive classes for different size options (sm, md, lg).
 * Persists user size preferences via cookies and provides computed classes for all table elements.
 */

import type { CellSize } from '@/components/table/types'
import type { Ref } from 'vue'
import { computed } from 'vue'

export const useTableSizing = (tableId: string, tableCellSize: Ref<CellSize>) => {
	const cellSizeOptions: CellSize[] = [ 'sm', 'md', 'lg' ]

	const smCellSize = 'text-xs px-6 py-1.5 font-normal'
	const mdCellSize = 'text-sm px-6 py-3 font-normal'
	const lgCellSize = 'text-sm h-[72px] px-6 py-4 font-normal'

	const smHeaderCellSize = 'text-xs px-6 py-1 font-normal'
	const mdHeaderCellSize = 'text-sm px-6 py-2 font-normal'
	const lgHeaderCellSize = 'text-sm px-6 py-3 font-normal'

	const smControlBarSize = 'py-3 px-3'
	const mdControlBarSize = 'py-3 px-3'
	const lgControlBarSize = 'py-4 px-6'

	const smControlButtonSize = 'h-10'
	const mdControlButtonSize = 'h-10'
	const lgControlButtonSize = 'h-12'

	const smDropdownSize = 'top-[40px]'
	const mdDropdownSize = 'top-[40px]'
	const lgDropdownSize = 'top-[48px]'

	const smPageInputSize = 'w-12 h-8'
	const mdPageInputSize = 'w-12 h-8'
	const lgPageInputSize = 'w-12 h-8'

	const smTextSize = 'text-xs'
	const mdTextSize = 'text-sm'
	const lgTextSize = 'text-lg'

	const smContextMenuWrapperSize = 'h-[30px] w-7'
	const mdContextMenuWrapperSize = 'h-[45px] w-8'
	const lgContextMenuWrapperSize = 'h-[72px] w-10'

	const smContextMenuButtonSize = 'w-6 h-6'
	const mdContextMenuButtonSize = 'w-7 h-7'
	const lgContextMenuButtonSize = 'w-9 h-9'

	const userSelectedCellSize: Ref<CellSize | ''> = useCookie(
		`${tableId}CellSize`,
		{ default: () => '' }
	)

	const cellSize = computed((): CellSize => userSelectedCellSize.value !== '' ? userSelectedCellSize.value : tableCellSize.value)

	const tableDataClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smCellSize
			case 'md':
				return mdCellSize
			case 'lg':
				return lgCellSize
			default:
				return mdCellSize
		}
	})

	const tableColumnClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smHeaderCellSize
			case 'md':
				return mdHeaderCellSize
			case 'lg':
				return lgHeaderCellSize
			default:
				return mdHeaderCellSize
		}
	})

	const tableTextClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smTextSize
			case 'md':
				return mdTextSize
			case 'lg':
				return lgTextSize
			default:
				return mdTextSize
		}
	})

	const controlButtonClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smControlButtonSize
			case 'md':
				return mdControlButtonSize
			case 'lg':
				return lgControlButtonSize
			default:
				return mdControlButtonSize
		}
	})

	const controlBarClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smControlBarSize
			case 'md':
				return mdControlBarSize
			case 'lg':
				return lgControlBarSize
			default:
				return mdControlBarSize
		}
	})

	const dropdownClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smDropdownSize
			case 'md':
				return mdDropdownSize
			case 'lg':
				return lgDropdownSize
			default:
				return mdDropdownSize
		}
	})

	const pageInputClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smPageInputSize
			case 'md':
				return mdPageInputSize
			case 'lg':
				return lgPageInputSize
			default:
				return mdPageInputSize
		}
	})

	const contextMenuWrapperClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smContextMenuWrapperSize
			case 'md':
				return mdContextMenuWrapperSize
			case 'lg':
				return lgContextMenuWrapperSize
			default:
				return mdContextMenuWrapperSize
		}
	})

	const contextMenuButtonClasses = computed(() => {
		switch (cellSize.value) {
			case 'sm':
				return smContextMenuButtonSize
			case 'md':
				return mdContextMenuButtonSize
			case 'lg':
				return lgContextMenuButtonSize
			default:
				return mdContextMenuButtonSize
		}
	})

	return {
		tableDataClasses,
		tableColumnClasses,
		tableTextClasses,
		controlButtonClasses,
		contextMenuWrapperClasses,
		contextMenuButtonClasses,
		controlBarClasses,
		dropdownClasses,
		pageInputClasses,
		cellSize,
		cellSizeOptions,
		userSelectedCellSize
	}
}
